export interface SharedUpload { id: string; leaseId: string; expiresAt: string }

/** Account changes invalidate pending work as well as completed leases. */
export function createSharedUploadRegistry(
  upload: (file: File, signal: AbortSignal) => Promise<SharedUpload>,
  getEpoch: () => number,
  isCurrent: (epoch: number) => boolean,
) {
  const entries = new Map<string,Promise<SharedUpload>>();
  let controller = new AbortController();
  let digests = new WeakMap<File,Promise<string>>();
  function check(epoch: number, signal: AbortSignal) {
    if (!isCurrent(epoch) || signal.aborted) throw new Error('账号会话已切换，请重新选择图片');
  }
  async function get(file: File): Promise<SharedUpload> {
    const epoch=getEpoch(), signal=controller.signal;
    let digest=digests.get(file);
    if (!digest) {
      digest=file.arrayBuffer().then(bytes=>crypto.subtle.digest('SHA-256',bytes))
        .then(bytes=>Array.from(new Uint8Array(bytes),b=>b.toString(16).padStart(2,'0')).join(''));
      digests.set(file,digest);
    }
    const key=`${epoch}:${await digest}`;
    check(epoch,signal);
    let pending=entries.get(key);
    const cached=Boolean(pending);
    if (!pending) {
      pending=upload(file,signal);
      entries.set(key,pending);
    }
    try {
      const asset=await pending;
      check(epoch,signal);
      if (new Date(asset.expiresAt).getTime() <= Date.now()+60_000) {
        if (entries.get(key)===pending) entries.delete(key);
        if (cached) return get(file);
        throw new Error('素材上传凭证已过期，请重试');
      }
      return asset;
    } catch (error) {
      if (entries.get(key)===pending) entries.delete(key);
      throw error;
    }
  }
  return {
    get,
    async forget(assetId:string) {
      await Promise.allSettled([...entries].map(async([key,pending])=>{
        if((await pending).id===assetId&&entries.get(key)===pending)entries.delete(key);
      }));
    },
    clear() {
      controller.abort();
      controller=new AbortController();
      entries.clear();
      digests=new WeakMap();
    },
  };
}
